/**
 * A React hook that fires a callback whenever MutationObserver detects a change to its size
 *
 * @param target A React ref created by `useRef()` or an HTML element
 * @param callback Invoked with a single `ResizeObserverEntry` any time
 *   the `target` resizes
 */
import { RefObject, useLayoutEffect } from 'react'

import { getRefElement } from 'helpers/getRefElement'

export function useMutationObserver<T extends HTMLElement>(
  ref: RefObject<T | null> | T | null | undefined,
  callback: UseMutationObserverCallback,
) {
  useLayoutEffect(() => {
    const target = getRefElement(ref)
    if (!target) return () => {}

    const mutationObserver = new MutationObserver(callback)
    mutationObserver.observe(target as HTMLElement, {
      attributes: true,
      childList: true,
      subtree: true,
    })
    return () => mutationObserver.disconnect()
  }, [ref])
}

export type UseMutationObserverCallback = (events: MutationRecord[]) => any
